/**
 * SPACE's (Duolingo-like) gamification configuration on top of `gamification-core`.
 *
 * The kit is AGNOSTIC: here Space provides ITS rules (XP for completing
 * lessons, daily study streak, badges, quests). Everything is pure → it's
 * computed local-first on the client for immediate feedback, and the server re-verifies it (grading.rs).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "../gamification/badges";
import type { GamificationConfig } from "../gamification/engine";

/** Space's domain event types. */
export const SPACE_EVENTS = {
  lessonCompleted: "lesson.completed",
  lessonPerfect: "lesson.perfect",
  courseCompleted: "course.completed",
} as const;

/** Bonus for a perfect lesson (score = 1). */
export const PERFECT_BONUS_XP = 5;
/** XP for completing an entire course. */
export const COURSE_COMPLETE_XP = 50;
/** Base XP if a lesson doesn't declare `xp_reward`. */
export const DEFAULT_LESSON_XP = 10;
/** Daily quest goal: lessons per day. */
export const DAILY_LESSON_GOAL = 3;

/**
 * Space's config. A lesson's XP travels in `event.value` (the lesson's
 * authoritative `xp_reward`); perfect ones and completed courses add on top.
 */
export const spaceGamificationConfig: GamificationConfig = {
  xp: {
    [SPACE_EVENTS.lessonCompleted]: (e) => e.value ?? DEFAULT_LESSON_XP,
    [SPACE_EVENTS.lessonPerfect]: PERFECT_BONUS_XP,
    [SPACE_EVENTS.courseCompleted]: COURSE_COMPLETE_XP,
  },
  // Studying (completing any lesson) keeps the "study" streak alive.
  streakLines: {
    [SPACE_EVENTS.lessonCompleted]: ["study"],
  },
  badges: [
    {
      id: "first-lesson",
      title: "Primera lección",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 1),
    },
    {
      id: "ten-lessons",
      title: "10 lecciones",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 10),
    },
    {
      id: "hundred-lessons",
      title: "100 lecciones",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 100),
      bonusXp: 100,
    },
    { id: "streak-7", title: "Racha de 7 días", when: streakAtLeast("study", 7), bonusXp: 20 },
    { id: "streak-30", title: "Racha de 30 días", when: streakAtLeast("study", 30), bonusXp: 100 },
    {
      id: "first-course",
      title: "Primer curso completado",
      when: counterAtLeast(SPACE_EVENTS.courseCompleted, 1),
    },
    { id: "scholar", title: "Estudiante (nivel 5)", when: levelAtLeast(5) },
    { id: "sage", title: "Sabio (nivel 10)", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-lessons",
      title: "3 lecciones hoy",
      period: "daily",
      goals: [{ metric: SPACE_EVENTS.lessonCompleted, target: DAILY_LESSON_GOAL }],
      bonusXp: 15,
    },
  ],
};
